'use client';


import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';
import { useLanguage } from '@/contexts/LanguageContext';

interface InvestmentSpecialCardProps {
  special: {
    id: string;
    title: string;
    category: string;
    minInvestment: string;
    targetRoi: string;
    roiPercentage: number;
    availability: 'limited' | 'open' | 'closing-soon';
    image: string;
    alt: string;
    riskLevel: 'no-risk' | 'low' | 'medium' | 'high';
    duration: string;
    spotsAvailable: number;
    highlights: string[];
    deadline: string;
  };
  onLearnMore: (specialId: string) => void;
}

const InvestmentSpecialCard = ({ special, onLearnMore }: InvestmentSpecialCardProps) => {
  const { t } = useLanguage();
  const availabilityConfig = {
    'limited': { color: 'bg-warning', text: t('investmentCard.limitedSpots'), icon: 'ExclamationTriangleIcon' },
    'open': { color: 'bg-success', text: t('investmentCard.open'), icon: 'CheckCircleIcon' },
    'closing-soon': { color: 'bg-destructive', text: t('investmentCard.closingSoon'), icon: 'ClockIcon' },
  };

  const riskConfig = {
    'no-risk': { color: 'text-blue-600', text: t('investmentCard.noRisk') },
    'low': { color: 'text-success', text: t('investmentCard.lowRisk') },
    'medium': { color: 'text-warning', text: t('investmentCard.mediumRisk') },
    'high': { color: 'text-destructive', text: t('investmentCard.highRisk') },
  };

  const currentAvailability = availabilityConfig[special.availability];
  const currentRisk = riskConfig[special.riskLevel];

  return (
    <div className="bg-card rounded-lg shadow-card hover:shadow-hover transition-all duration-300 overflow-hidden border-2 border-primary/20 hover:border-primary/40">
      <div className="relative h-48 overflow-hidden">
        <AppImage
          src={special.image}
          alt={special.alt}
          className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
        />
        <div className="absolute top-3 right-3 flex items-center space-x-2">
          <span className={`${currentAvailability.color} text-white px-3 py-1 rounded-full text-xs font-semibold flex items-center space-x-1 animate-pulse`}>
            <Icon name={currentAvailability.icon as any} size={14} variant="solid" />
            <span>{currentAvailability.text}</span>
          </span>
        </div>
        <div className="absolute top-3 left-3 bg-primary text-primary-foreground px-3 py-1.5 rounded-md text-xs font-bold uppercase tracking-wide">
          {t('investmentCard.newOpportunity')}
        </div>
        <div className="absolute bottom-3 left-3 bg-card/90 backdrop-blur-sm text-foreground px-3 py-1 rounded-md text-xs font-semibold">
          {special.category}
        </div>
      </div>

      <div className="p-5">
        <h3 className="text-lg font-heading font-bold text-foreground mb-2 line-clamp-2">
          {special.title}
        </h3>

        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-1 text-muted-foreground text-sm">
            <Icon name="CalendarIcon" size={16} variant="outline" />
            <span>{t('investmentCard.deadline')}: {special.deadline}</span>
          </div>
          <div className="flex items-center space-x-1 text-sm">
            <Icon name="ShieldCheckIcon" size={16} variant="outline" className={currentRisk.color} />
            <span className={currentRisk.color}>{currentRisk.text}</span>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3 mb-4">
          <div className="bg-muted rounded-md p-3">
            <p className="text-xs text-muted-foreground mb-1">{t('investmentCard.investmentAmount')}</p>
            <p className="text-base font-heading font-bold text-foreground">{special.minInvestment}</p>
          </div>
          <div className="bg-primary/10 rounded-md p-3">
            <p className="text-xs text-muted-foreground mb-1">{t('investmentCard.amountYouReceive')}</p>
            <p className="text-base font-heading font-bold text-primary">{special.targetRoi}</p>
          </div>
        </div>

        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-2">
            <Icon name="ClockIcon" size={16} variant="outline" className="text-muted-foreground" />
            <span className="text-sm text-muted-foreground">{special.duration}</span>
          </div>
        </div>

        <div className="flex items-center space-x-2">
          <button
            onClick={() => window.open('https://wa.me/17868872610?text=Hi%2C%20I%27m%20interested%20in%20this%20investment%20special', '_blank')}
            className="w-full px-4 py-2 bg-primary text-primary-foreground rounded-md text-sm font-medium hover:bg-primary/90 transition-colors duration-300 flex items-center justify-center space-x-1 shadow-md"
          >
            <Icon name="ArrowRightIcon" size={16} variant="outline" />
            <span>{t('investmentCard.investNow')}</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default InvestmentSpecialCard;